import { useState, type ReactNode } from 'react'
import { QrCode, X } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import { useFieldControl } from './fieldContext'
import { controlBorder } from './controlStyles'
import { ScannerSheet, type ScanMeta, type ScanValidator } from './scanner/ScannerSheet'

export interface ScanFieldProps {
  value: string | null
  onChange: (code: string | null, meta?: ScanMeta) => void
  /**
   * Check the code's kind / prefix / roll before accepting it. Return an error message to refuse
   * (the phone beeps and stays on the scanner), or null to accept.
   *   validate={(c) => c.startsWith(roll.payload_prefix) ? null : 'Not from your roll'}
   */
  validate?: ScanValidator
  /** Normalise before validation, e.g. expand a typed short number to the full code. */
  normalize?: (code: string) => string
  sheetTitle?: ReactNode
  placeholder?: ReactNode
  disabled?: boolean
  /** Extra info under the code, e.g. "250 tags in hand". */
  detail?: ReactNode
}

/** A field whose value comes from scanning a QR (with manual entry as the fallback). */
export function ScanField({
  value,
  onChange,
  validate,
  normalize,
  sheetTitle,
  placeholder,
  disabled,
  detail,
}: ScanFieldProps) {
  const { t } = useI18n()
  const field = useFieldControl()
  const [open, setOpen] = useState(false)

  return (
    <>
      <div
        className={cn(
          'flex min-h-touch items-center gap-2 rounded-chip border bg-surface pl-3.5 pr-1.5',
          controlBorder(field?.invalid ?? false),
          disabled && 'bg-paper-sunk',
        )}
      >
        <div className="min-w-0 flex-1 py-2">
          {value ? (
            <span className="block truncate font-mono text-[16px] font-medium text-ink">
              {value}
            </span>
          ) : (
            <span className="block text-[16px] text-muted">{placeholder ?? t('scan.tap')}</span>
          )}
          {detail && <span className="block text-[12px] text-muted">{detail}</span>}
        </div>
        {value && !disabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={t('pin.clear')}
            className="inline-flex h-10 w-10 items-center justify-center rounded-chip text-muted hover:bg-ink/5"
          >
            <X aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          id={field?.id}
          aria-describedby={field?.describedBy}
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="inline-flex h-10 items-center gap-1.5 rounded-chip bg-ink px-3 font-mono text-[12px] font-bold uppercase tracking-label text-paper disabled:opacity-40"
        >
          <QrCode size={20} aria-hidden="true" />
          {/* With a code shown, the button shrinks to its icon so the code is readable at 360 px. */}
          <span className={value ? 'sr-only' : undefined}>{t('scan.title')}</span>
        </button>
      </div>
      <ScannerSheet
        open={open}
        onClose={() => setOpen(false)}
        onScan={(c, meta) => onChange(c, meta)}
        validate={validate}
        normalize={normalize}
        title={sheetTitle}
      />
    </>
  )
}
